import React, {useEffect, useRef, useState} from "react";
import { useReactToPrint } from "react-to-print";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import {useDispatch, useSelector} from "react-redux";
import ERPSTransactionDetails from "../custom/ERPSTransactionDetails";
import actions from "../../actions";
import ConfirmationMessage from "../custom/containers/ConfirmationMesssage";
import ErrorMessage from "../custom/containers/ErrorMessage";
import DialogHeader from "../custom/inputs/ERPSBaseModelDialog";
import { Regex, RoleType}  from "../../constants/sharedConstants.js";
import { checkFieldValue, checkLengthAndRegex, formatUTCDateTime } from "../../utils/helper";
import { Dialog, DialogActions, DialogContent, HStack, ResponsiveBox, Stack, TextField } from "../primitives";

const TraceQueueDetails = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        location: { state },
    } = props;

    const [openDialog, setDialogOpen] = useState({ isOpen: false });
    const [loading, setLoading] = useState(null);
    const [comment, setComment] = useState(""); 
    const [commentError, setCommentError] = useState("");
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [traceMessages, setTraceMessages] = useState(null);
    const [isModified, setIsModified] = useState(false);

    const traceQueueData = useSelector(
        (state) => state.traceManagementReducer?.traceQueueData?.data
    );
    const [tracePayment, setTracePayment] = useState(traceQueueData ? traceQueueData.find(({ id }) => id === state.id) : {});

    const tqReducer = useSelector( (state) => state.traceManagementReducer );

    const user = useSelector((state) => state.loginUserReducer.user);
    const [roleType] = useState(user.role.role_type);

    const errorRef = useRef();
    const successRef = useRef();
    const printComponentRef = useRef();


    const fiWPOrERPAdminRoles = [RoleType.WP_FI_ADMIN.type, RoleType.WP_FI_USER.type, RoleType.ERPADMIN.type, RoleType.BILLADMIN.type];


    useEffect(() => {
        if (showSuccessMessage && successRef.current)
            successRef.current.focus();
    }, [showSuccessMessage]);
        

    useEffect(() => {
        if (errorRef.current)
            errorRef.current.focus();
    }, [errorMessage?.length]);

    
    useEffect(() => {
        loadMessages();

        return onExit;  
    }, []);

    const onExit = () => {
        dispatch({ type: "READ_TRACE_MESSAGES_SUCCESS", data: null });
        errorRef.current = null;
        successRef.current = null;
        if (printComponentRef)
            printComponentRef.current = null;
    }

    const loadMessages = async () => {
        setLoading(true);

        dispatch(
            actions.traceManagementActions.readTraceMessages(
                // { correlation_id: tracePayment.correlation_id },
                { trace_id: tracePayment.trace_id },
                { setLoading }
            )
        );
    }

    useEffect(() => {
        if (loading === false) {
            if (tqReducer.action.startsWith("READ_TRACE_MESSAGES")) {
                if (tqReducer.readTraceMessagesError) {
                    setErrorMessage(tqReducer.readTraceMessagesError);
                } else {
                    if (setMessageActivity(tqReducer.readTraceMessagesData?.data))
                        dispatch({ type: "READ_TRACE_MESSAGES_SUCCESS", data: null });
                }
            } else if (tqReducer.action.startsWith("SEND_TRACE_FOR_TRACE_QUEUE")) {
                updateAfterSendOrClose(true);
            } else if (tqReducer.action.startsWith("CLOSE_TRACE_FOR_TRACE_QUEUE")) {
                updateAfterSendOrClose(false);
            }
        }
    }, [loading]);

    const updateAfterSendOrClose = (isSend) => {
        if (tqReducer.closeOrSendTraceQueueError) {
            setErrorMessage(tqReducer.closeOrSendTraceQueueError);
        } else {
            let updatedTracePayment = tqReducer.closeOrSendTraceQueueData?.data?.data;
            if (updatedTracePayment) {
                setMessageActivity(updatedTracePayment.message_acitivity);
                if (!isSend) {
                    setSuccessMessage(t("erps.tracequeue.message.traceClosed"));
                } else if (fiWPOrERPAdminRoles.includes(roleType)) {
                    setSuccessMessage(t("erps.tracequeue.message.forFI.messageSent.long"));
                } else {
                    setSuccessMessage(t("erps.tracequeue.message.forBiller.messageSent.long"));
                }
                setShowSuccessMessage(true);
                setIsModified(true);

                // const { awaiting_response_from, transaction_status, updatedDate, updatedBy } = updatedTracePayment;
                // setTracePayment( { ...tracePayment, awaiting_response_from, transaction_status, updatedDate, updatedBy} );
                const {transaction_status, updatedDate, updatedBy } = updatedTracePayment;
                setTracePayment( { ...tracePayment, transaction_status, updatedDate, updatedBy} );
                dispatch({ type: tqReducer.action, data: null });
            }
        }
    };

    const setMessageActivity = (messageList) => {
        if (!messageList?.length)
            return false;
        let traceMsgs = messageList.map(
            (msg, idx) => ({ id: idx, userId: msg.updatedBy, userName: msg.user_name, date: formatUTCDateTime(msg.updatedDate), comment: msg.comment })
        );
        setTraceMessages(traceMsgs);
        return true;
    };

    const handleCommentChange = (e) => {
        const value = e.target.value;
        let errMsg = checkLengthAndRegex(value, "erps.common.label.comment", 300, Regex.regularText);
        // one invalid character will show in the field, but further input isn't allowed until the invalid character is removed
        setCommentError(errMsg);
        if ("" === errMsg || Regex.regularText.test(comment))  // if either current value is valid or previous value is valid
            setComment(value);
    };

    const handlePrint = useReactToPrint({
        content: () => printComponentRef?.current
    });

    function handleSendButton() {
        let errorMsg = checkFieldValue(comment, "erps.common.label.comment", true, (v) => Regex.regularText.test(v));
        setCommentError(errorMsg);
        if ("" === errorMsg)
            setDialogOpen({ isOpen: true, action: "sendTrace" });
    }

    function handleSendOrClose() {
        setDialogOpen({ isOpen: false });
        let isSend = (openDialog.action === "sendTrace");
        if (!isSend && openDialog.action !== "closeTrace") {
            console.error("Unrecognized openDialog.action: " + openDialog.action); // This should never happen, keeping it just in case
            return;
        }

        setErrorMessage(null)
        setLoading(true);
        setShowSuccessMessage(false);

        let request = {
            trace_id: tracePayment.trace_id,
            // correlation_id: tracePayment.correlation_id,
            fi_number: tracePayment.fi_number,
            updated_by: user.userId,
            comment : comment,
        };

        let action = isSend ? actions.traceManagementActions.sendTraceQueue : actions.traceManagementActions.closeTraceQueue;
        dispatch( action(request, {setLoading}) );
        setDialogOpen({ isOpen: false });
    }

    function isSendCommentAllowed() {
        if (state.isReadonly || isModified)
            return false;
        let transactionStatuts = tracePayment.transaction_status;
        if (fiWPOrERPAdminRoles.includes(roleType) && transactionStatuts === "I")
            return true;
        if ((roleType === RoleType.BILLER_ADMIN.type || roleType === RoleType.BILLER_USER.type) && transactionStatuts === "M")
            return true;
        return false;
    }

    function isCloseButtonAllowed() {
        if (state.isReadonly || isModified)  // Once a comment was sent or trace closed
            return false;
        if (fiWPOrERPAdminRoles.includes(roleType) && tracePayment.transaction_status === "I")
            return true;
        return false;
    }
    
    return (
        <div role="main">
            {showSuccessMessage && (
                <ResponsiveBox className="success-message-container" mt={2}>
                    <ConfirmationMessage
                        message={successMessage}
                        setShowMessage={setShowSuccessMessage}
                        inputRef={successRef}
                    />
                </ResponsiveBox>
            )}

            <ErrorMessage 
              error={errorMessage}
              errorRef={errorRef}
              mt={2}
            />
            <ResponsiveBox sx={{ ml: 3 }}>
                <Breadcrumb
                    {...props}
                    fromDetailPage={true}
                />
            </ResponsiveBox>

            <ERPSTransactionDetails data={tracePayment} traceMessageList = {traceMessages} ref={printComponentRef}/>
                   
            <ResponsiveBox sx={{ ml: 3, mt: 3, width: "80%" }}>
                {isSendCommentAllowed() && (
                    <TextField
                        label={t("erps.common.label.comment")}
                        required={true}
                        fullWidth
                        value={comment}
                        onChange={handleCommentChange}
                        error={!!commentError}
                        helperText={commentError || `${comment.length}/300 characters`}
                        inputProps={{ maxLength: 300 }}
                    />
                    )}
                </ResponsiveBox>

            <HStack spacing={2} ml={3}>
                <ERPSBaseButton
                    variant="secondary"
                    className="button_with_margin48"
                    label={t("erps.newuser.button.back", "Back")}
                    onClick={() =>
                        state.isReadonly ? props.history.push("/trace-history/",
                                                              {fromDetailPage: true})
                                         : props.history.push("/trace-queue/",
                                                              {fromDetailPage: true, isModified: isModified})
                    }
                />

                {/* Close Trace Button */}
                {isCloseButtonAllowed() && (
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.trace.button.closeTrace", "Close Trace")}
                        onClick={() => setDialogOpen({ isOpen: true, action: "closeTrace" }) }
                        loading={loading}
                        isDisabled={isModified}
                    />
                )}

                {isSendCommentAllowed() && (
                    // Send Button
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.trace.button.send", "Send")}
                        onClick={handleSendButton}
                        loading={loading}
                        isDisabled={isModified}
                    />
                )}
                {state.isReadonly && (
                    <ERPSBaseButton                  // PRINT
                        variant="primary"
                        className="button_with_margin48"
                        label={t("erps.transactionEnquiry.page.transactionDetails.button.print")}
                        onClick={handlePrint}
                        loading={loading}
                    />
                )}
            </HStack>

            <Dialog
                open={openDialog?.isOpen}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth={true}
            >
                <DialogHeader
                    id="alert-dialog-title"
                    onClose={() => setDialogOpen({ isOpen: false }) }
                    className={"box-dialog-header"}
                >
                    {t("erps.common.label.confirmation")}
                </DialogHeader>

                <DialogContent
                        id="alert-dialog-description"
                        className={"dialog-content-size"} >
                    {openDialog?.action === "closeTrace" 
                    ? t("erps.traceQueue.page.confirm.closeTraceQueueDetails.dialog.content")
                    : openDialog?.action === "sendTrace" 
                    ? t("erps.traceQueue.page.confirm.sendTraceQueueDetails.dialog.content")
                    : null}
                </DialogContent>
               
                <DialogActions className={"box-dialog-buttons"}>
                    <ERPSBaseButton
                        variant="secondary"
                        className="button_with_zeromargin"
                        onClick={() => setDialogOpen({ isOpen: false })}
                        label={t("erps.common.button.cancel")}
                    />
                    <ERPSBaseButton
                        variant="primary"
                        className="button_with_zeromargin"
                        onClick={handleSendOrClose}
                        label={t("erps.welcome.page.button.Ok")}
                    />
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default withSidebar(TraceQueueDetails);
